/**
 * Aaramya curated upsell (PDP + cart drawer). Active products only.
 * Skips bundle suggestions when 2+ items already overlap (net-new ≤ 1).
 */
(function () {
  let pdpMediaAutoTimer = null;
  let pdpMediaLastAdvanceAt = 0;
  const BUNDLE_COMPONENTS = {
    'complete-hair-ritual-kit': [
      'hair-toner-100ml',
      'magical-hair-oil',
      'nature-s-secret-homemade-herbal-hair-shampoo',
    ],
    'hair-care-duo-oil-shampoo': [
      'magical-hair-oil',
      'nature-s-secret-homemade-herbal-hair-shampoo',
    ],
    'daily-hair-refresh-kit': ['hair-toner-100ml', 'magical-hair-oil'],
    'aaramya-essentials-kit': [
      'aaramya-herbal-facewash',
      'hair-toner-100ml',
      'magical-hair-oil',
      'nature-s-secret-homemade-herbal-hair-shampoo',
    ],
  };

  const PDP_RULES = {
    'nature-s-secret-homemade-herbal-hair-shampoo': {
      heading: 'Complete your ritual',
      suggest: ['hair-toner-100ml', 'magical-hair-oil'],
    },
    'hair-toner-100ml': {
      heading: 'Complete your ritual',
      suggest: ['magical-hair-oil', 'nature-s-secret-homemade-herbal-hair-shampoo'],
    },
    'magical-hair-oil': {
      heading: 'Complete your ritual',
      suggest: [
        'nature-s-secret-homemade-herbal-hair-shampoo',
        'hair-toner-100ml',
      ],
    },
    'aaramya-herbal-facewash': {
      heading: 'Start your hair ritual',
      suggest: ['hair-toner-100ml', 'complete-hair-ritual-kit'],
    },
    'complete-hair-ritual-kit': {
      heading: 'Pair with face & home care',
      suggest: ['aaramya-herbal-facewash', 'lemon-dishwash'],
    },
    'hair-care-duo-oil-shampoo': {
      heading: 'Complete your ritual',
      suggest: ['hair-toner-100ml', 'complete-hair-ritual-kit'],
    },
    'daily-hair-refresh-kit': {
      heading: 'Complete your ritual',
      suggest: [
        'nature-s-secret-homemade-herbal-hair-shampoo',
        'complete-hair-ritual-kit',
      ],
    },
    'aaramya-essentials-kit': {
      heading: 'Complete your home care',
      suggest: ['lemon-dishwash'],
    },
    _default: {
      heading: 'Complete your ritual',
      suggest: ['magical-hair-oil', 'hair-toner-100ml'],
    },
  };

  const PDP_FALLBACK_POOL = [
    'hair-toner-100ml',
    'magical-hair-oil',
    'nature-s-secret-homemade-herbal-hair-shampoo',
    'aaramya-herbal-facewash',
    'lemon-dishwash',
    'complete-hair-ritual-kit',
  ];

  const CART_PRIORITY = [
    'hair-toner-100ml',
    'magical-hair-oil',
    'nature-s-secret-homemade-herbal-hair-shampoo',
    'complete-hair-ritual-kit',
    'daily-hair-refresh-kit',
    'hair-care-duo-oil-shampoo',
    'aaramya-herbal-facewash',
    'aaramya-essentials-kit',
    'lemon-dishwash',
  ];

  const MONEY = (cents) =>
    '₹' + Math.ceil(cents / 100).toLocaleString('en-IN');

  function coveredForHandle(handle) {
    const set = new Set([handle]);
    (BUNDLE_COMPONENTS[handle] || []).forEach((h) => set.add(h));
    return set;
  }

  /** Block only true duplicate bundles (e.g. Essentials when full hair line already owned). */
  function allowSuggestion(covered, suggestHandle) {
    if (covered.has(suggestHandle)) return false;
    if (suggestHandle === 'complete-hair-ritual-kit') return true;
    if (suggestHandle === 'aaramya-essentials-kit') {
      const hair = [
        'hair-toner-100ml',
        'magical-hair-oil',
        'nature-s-secret-homemade-herbal-hair-shampoo',
      ];
      const hairOwned = hair.filter((h) => covered.has(h)).length;
      if (covered.has('complete-hair-ritual-kit') || hairOwned >= 3) return false;
    }
    const comps = BUNDLE_COMPONENTS[suggestHandle];
    if (!comps) return true;
    const netNew = comps.filter((h) => !covered.has(h));
    if (netNew.length === 0) return false;
    return true;
  }

  function lineHandle(item) {
    if (item.handle) return item.handle;
    if (item.product_handle) return item.product_handle;
    const url = item.url || '';
    const m = String(url).match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  function coveredFromCart(cart) {
    const covered = new Set();
    (cart.items || []).forEach((item) => {
      const h = lineHandle(item);
      if (h) covered.add(h);
      const comps = BUNDLE_COMPONENTS[h];
      if (comps) comps.forEach((c) => covered.add(c));
    });
    return covered;
  }

  function pdpPicks(handle) {
    const rule = PDP_RULES[handle] || PDP_RULES._default;
    const covered = coveredForHandle(handle);
    const suggest = [];
    for (const h of rule.suggest) {
      if (allowSuggestion(covered, h)) suggest.push(h);
    }
    for (const h of PDP_FALLBACK_POOL) {
      if (suggest.length >= 2) break;
      if (suggest.includes(h)) continue;
      if (allowSuggestion(covered, h)) suggest.push(h);
    }
    return { heading: rule.heading, suggest: suggest.slice(0, 2) };
  }

  function cartPicks(cart, limit) {
    const covered = coveredFromCart(cart);
    const inCart = new Set((cart.items || []).map((i) => lineHandle(i)).filter(Boolean));

    if (inCart.has('aaramya-essentials-kit')) {
      return inCart.has('lemon-dishwash') ? [] : ['lemon-dishwash'];
    }

    if (inCart.has('complete-hair-ritual-kit')) {
      const picks = [];
      for (const h of ['aaramya-herbal-facewash', 'lemon-dishwash']) {
        if (picks.length >= limit) break;
        if (!covered.has(h) && !inCart.has(h)) picks.push(h);
      }
      return picks;
    }

    const picks = [];
    for (const h of CART_PRIORITY) {
      if (picks.length >= limit) break;
      if (!allowSuggestion(covered, h)) continue;
      if (h === 'lemon-dishwash') continue;
      picks.push(h);
    }
    return picks;
  }

  function productHandleFromPath() {
    const m = location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  function fetchProduct(handle) {
    return fetch(`/products/${handle}.js`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }

  function formatInr(cents) {
    return '₹' + (Number(cents) / 100).toLocaleString('en-IN');
  }

  function formatProductPriceHtml(variant) {
    if (!variant || variant.price == null) return '';
    const current = formatInr(variant.price);
    const compare = variant.compare_at_price;
    if (compare && Number(compare) > Number(variant.price)) {
      return `<span class="aa-product-price"><span class="aa-product-price__compare">${formatInr(compare)}</span><span class="aa-product-price__current">${current}</span></span>`;
    }
    return `<span class="aa-product-price"><span class="aa-product-price__current">${current}</span></span>`;
  }

  function mapProduct(p) {
    if (!p || !p.available) return null;
    const v = p.variants && p.variants[0];
    if (!v || !v.id) return null;
    return {
      handle: p.handle,
      title: p.title,
      url: `/products/${p.handle}`,
      price: formatProductPriceHtml(v),
      featured_image: p.featured_image || (p.images && p.images[0]) || '',
      variantId: v.id,
      variantUrl: `/products/${p.handle}?variant=${v.id}`,
    };
  }

  const RITUAL_HANDLES = [
    'hair-toner-100ml',
    'magical-hair-oil',
    'nature-s-secret-homemade-herbal-hair-shampoo',
  ];

  function updateRitualProgress(cart) {
    const covered = coveredFromCart(cart);
    let done = 0;
    RITUAL_HANDLES.forEach((h) => {
      if (covered.has(h)) done += 1;
    });
    document.querySelectorAll('[data-aa-ritual-steps]').forEach((root) => {
      root.dataset.stepsDone = String(done);
      root.querySelectorAll('[data-ritual-handle]').forEach((chip) => {
        const isDone = covered.has(chip.dataset.ritualHandle);
        chip.classList.toggle('is-done', isDone);
        chip.classList.toggle('is-pending', !isDone);
      });
      const meta = root.querySelector('.aa-ritual-steps__meta');
      if (meta) {
        if (done >= 3) {
          meta.hidden = true;
          meta.textContent = '';
        } else {
          meta.hidden = false;
          meta.textContent = `${done} of 3 in cart`;
        }
      }
    });
  }

  function renderPdpList(list, products) {
    list.innerHTML = '';
    products.forEach((p) => {
      if (!p) return;
      const li = document.createElement('li');
      li.className = 'aa-pdp-xsell__item';
      const img = p.featured_image
        ? `<a href="${p.url}" class="aa-pdp-xsell__thumb"><img src="${p.featured_image}" alt="" loading="lazy" width="56" height="56"></a>`
        : `<a href="${p.url}" class="aa-pdp-xsell__thumb"></a>`;
      li.innerHTML = `${img}<div><p class="aa-pdp-xsell__name"><a href="${p.url}">${p.title}</a></p><p class="aa-pdp-xsell__price">${p.price}</p></div><a href="${p.variantUrl}" class="aa-pdp-xsell__btn">Add</a>`;
      list.appendChild(li);
    });
  }

  function removeStaleRecommendationBlocks() {
    document
      .querySelectorAll(
        'product-recommendations, .product-recommendations-wrapper, [data-testid="product-recommendations-block"]'
      )
      .forEach((el) => {
        const inDetails = el.closest('.product-details, [class*="product-details"]');
        if (inDetails) el.remove();
      });
  }

  function findPdpUpsellAnchor() {
    const details = document.querySelector(
      '.product-details, [data-testid="product-information-details"]'
    );
    if (details) {
      const content =
        details.querySelector('.group-block-content') || details;
      const blocks = [...content.children].filter(
        (el) => el.querySelector?.('.buy-buttons-block') || el.classList?.contains('buy-buttons-block') || el.textContent?.trim()
      );
      if (blocks.length) return blocks[blocks.length - 1];
    }
    const buy = document.querySelector('.buy-buttons-block');
    if (!buy) return null;
    let last = buy;
    let el = buy;
    while (el.nextElementSibling) {
      el = el.nextElementSibling;
      last = el;
    }
    return last;
  }

  function injectPdpCrossSell() {
    if (!location.pathname.includes('/products/')) return;
    removeStaleRecommendationBlocks();
    document.querySelectorAll('[data-aa-pdp-xsell]').forEach((el) => el.remove());
  }

  function renderDrawerUpsellCard(p) {
    return `<article class="aa-drawer-up__card">
      <a href="${p.url}" class="aa-drawer-up__img">${
        p.featured_image
          ? `<img src="${p.featured_image}" alt="" loading="lazy" width="152" height="152">`
          : ''
      }</a>
      <div class="aa-drawer-up__body">
        <p class="aa-drawer-up__name"><a href="${p.url}">${p.title}</a></p>
        <p class="aa-drawer-up__price">${p.price}</p>
        <button type="button" class="aa-drawer-up__btn" data-aa-drawer-add data-variant-id="${p.variantId}">Add</button>
      </div>
    </article>`;
  }

  function getDrawerSectionId() {
    const el = document.querySelector('cart-items-component[data-drawer]');
    return el?.dataset?.sectionId || '';
  }

  function ensureDrawerUpsellMount() {
    const wrap = document.querySelector('[data-aa-drawer-upsell]');
    if (!wrap) return null;
    return wrap.querySelector('[data-aa-drawer-upsell-track]');
  }

  function dispatchCartUpdate(response, variantId) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: {},
          sourceId: String(variantId),
          data: {
            source: 'aaramya-drawer-upsell',
            itemCount: 1,
            variantId: String(variantId),
            sections: response.sections,
          },
        },
      })
    );
  }

  function addVariantFromDrawer(variantId) {
    const sectionId = getDrawerSectionId();
    const fd = new FormData();
    fd.append('id', String(variantId));
    fd.append('quantity', '1');
    if (sectionId) {
      fd.append('sections', sectionId);
      fd.append('sections_url', window.location.pathname);
    }
    const url =
      (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) ||
      '/cart/add.js';
    return fetch(url, {
      method: 'POST',
      body: fd,
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
    }).then((r) => r.json());
  }

  function bindDrawerAddButtons(root) {
    (root || document).querySelectorAll('[data-aa-drawer-add]').forEach((btn) => {
      if (btn.dataset.aaBound) return;
      btn.dataset.aaBound = '1';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        let variantId = btn.dataset.variantId;
        if (!variantId && btn.href) {
          const url = new URL(btn.href, location.origin);
          variantId = url.searchParams.get('variant') || url.searchParams.get('id');
        }
        if (!variantId) return;
        btn.classList.add('is-loading');
        btn.disabled = true;
        const prev = btn.textContent;
        btn.textContent = 'Adding…';
        addVariantFromDrawer(variantId)
          .then((response) => {
            if (response.status) throw new Error(response.message || 'Add failed');
            dispatchCartUpdate(response, variantId);
            btn.textContent = 'Added';
            window.setTimeout(() => {
              btn.textContent = prev;
              btn.disabled = false;
            }, 1200);
          })
          .catch(() => {
            btn.textContent = prev;
            btn.disabled = false;
          })
          .finally(() => btn.classList.remove('is-loading'));
      });
    });
  }

  function refreshCartTierBars() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((cart) => {
        const total = cart.total_price || 0;
        const t1 = 49900;
        const t2 = 99900;
        let msg = '';
        let pct = 0;
        let su = 0;
        let tierLevel = 0;
        if (total < t1) {
          msg = `Add ${MONEY(t1 - total)} more for free delivery`;
          pct = Math.round((total / t1) * 100);
        } else if (total < t2) {
          msg = `Free delivery unlocked — add ${MONEY(t2 - total)} more for an extra 5% off`;
          pct = Math.round((total / t2) * 100);
          su = 1;
          tierLevel = 1;
        } else {
          msg =
            "You've unlocked free delivery and an extra 5% off this order";
          pct = 100;
          su = 1;
          tierLevel = 2;
        }
        updateRitualProgress(cart);

        document.querySelectorAll('[data-aa-cart-tier-static]').forEach((el) => {
          const prev = parseInt(el.dataset.tierLevel || '0', 10);
          const m = el.querySelector('.aa-cart-tier__msg');
          const f = el.querySelector('.aa-cart-tier__fill');
          if (m) {
            m.textContent = msg;
            m.classList.toggle('is-success', su > 0);
          }
          if (f) f.style.width = pct + '%';
          el.dataset.tierLevel = String(tierLevel);
          el.dataset.cartTotal = String(total);
          if (tierLevel > prev) {
            el.classList.add('aa-cart-tier--celebrate');
            window.setTimeout(() => el.classList.remove('aa-cart-tier--celebrate'), 700);
          }
          el.querySelectorAll('.aa-cart-tier__milestone').forEach((ms, i) => {
            ms.classList.toggle('is-hit', tierLevel > i);
          });
        });

        const wrap = document.querySelector('[data-aa-drawer-upsell]');
        const track = ensureDrawerUpsellMount();
        if (!track || !wrap) return;

        if (!cart.item_count) {
          wrap.setAttribute('hidden', '');
          return;
        }

        const picks = cartPicks(cart, 3);
        if (!picks.length) {
          wrap.setAttribute('hidden', '');
          return;
        }

        wrap.removeAttribute('hidden');
        const headingEl = wrap.querySelector('[data-aa-upsell-heading]');
        if (headingEl) headingEl.textContent = 'Complete your ritual';
        Promise.all(picks.map((h) => fetchProduct(h))).then((rows) => {
          const products = rows.map(mapProduct).filter(Boolean);
          if (!products.length) {
            wrap.setAttribute('hidden', '');
            return;
          }
          track.innerHTML = products.map(renderDrawerUpsellCard).join('');
          bindDrawerAddButtons(track);
        });
      })
      .catch(() => {});
  }

  function initVideoSoundLabels() {
    document.addEventListener(
      'volumechange',
      (e) => {
        if (e.target.tagName !== 'VIDEO') return;
        const wrap = e.target.closest(
          '.aa-founder__media, .aa-pvideo__wrap, .aa-svideo__player-wrap, .aa-dist__media-wrap, .aa-ingspot__media'
        );
        const label = wrap && wrap.querySelector('.aa-video-sound-label');
        if (label) label.classList.toggle('is-hidden', !e.target.muted);
      },
      true
    );
    document.querySelectorAll('video[muted]').forEach((video) => {
      const wrap = video.closest(
        '.aa-founder__media, .aa-pvideo__wrap, .aa-svideo__player-wrap, .aa-dist__media-wrap, .aa-ingspot__media'
      );
      const label = wrap && wrap.querySelector('.aa-video-sound-label');
      if (label && !video.muted) label.classList.add('is-hidden');
    });
  }

  function initPdpMediaAutoplayFallback() {
    if (!location.pathname.includes('/products/')) return;
    if (pdpMediaAutoTimer) return;
    pdpMediaAutoTimer = window.setInterval(() => {
      const gallery = document.querySelector('media-gallery[data-presentation="carousel"]');
      if (!gallery) return;
      if (document.hidden) return;
      if (gallery.matches(':hover')) return;
      const slideshow = gallery.querySelector('slideshow-component');
      if (!slideshow) return;
      const speedSec = parseInt(slideshow.getAttribute('autoplay') || '5', 10) || 5;
      const now = Date.now();
      if (now - pdpMediaLastAdvanceAt < speedSec * 1000) return;
      pdpMediaLastAdvanceAt = now;
      if (typeof slideshow.next === 'function') {
        slideshow.next(undefined, { animate: true });
        return;
      }
      const nextBtn = gallery.querySelector('.slideshow-control--next, [on\\:click*=\"next\"]');
      if (nextBtn) nextBtn.click();
    }, 1000);
  }

  function initBundleBorrowedProof() {
    const handle = productHandleFromPath();
    if (!handle || !BUNDLE_COMPONENTS[handle]) return;
    const insert = () => {
      if (document.querySelector('[data-aa-borrowed-proof]')) return true;
      const details = document.querySelector(
        '.product-details, [data-testid="product-information-details"]'
      );
      if (!details) return false;
      const proof = document.createElement('p');
      proof.setAttribute('data-aa-borrowed-proof', '1');
      proof.className = 'aa-borrowed-proof';
      proof.innerHTML =
        '<span class="aa-borrowed-proof__stars" aria-hidden="true">★★★★★</span> Built from our most-reviewed products';
      const badge = details.querySelector(
        '.jdgm-widget, .jdgm-prev-badge, .jdgm-preview-badge'
      );
      if (badge && badge.parentNode) {
        badge.parentNode.insertBefore(proof, badge.nextSibling);
        return true;
      }
      const buy = details.querySelector('.buy-buttons-block');
      if (buy && buy.parentNode) {
        buy.parentNode.insertBefore(proof, buy);
        return true;
      }
      return false;
    };
    if (insert()) return;
    let tries = 0;
    const iv = window.setInterval(() => {
      tries += 1;
      if (insert() || tries > 12) window.clearInterval(iv);
    }, 400);
  }

  function init() {
    injectPdpCrossSell();
    refreshCartTierBars();
    bindDrawerAddButtons(document);
    initVideoSoundLabels();
    initPdpMediaAutoplayFallback();
    initBundleBorrowedProof();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  function onCartRefresh() {
    refreshCartTierBars();
    setTimeout(refreshCartTierBars, 150);
    setTimeout(refreshCartTierBars, 500);
  }

  document.addEventListener('cart:update', onCartRefresh);
  document.addEventListener('cart:change', onCartRefresh);
  document.addEventListener('cart:add', onCartRefresh);
  document.addEventListener('dialog:open', () => {
    if (document.querySelector('cart-drawer-component[open], cart-drawer-component dialog[open]')) {
      onCartRefresh();
    }
  });
})();
